// OL-203: the people who sign a document, in signing order. Signature and
// initials fields point at a signer by `signer_key`; the order number sent to
// the backend is the signer's position in this list.

export const RECIPIENT_KEY = 'recipient';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SIGNABLE_TYPES = new Set(['signature', 'initials']);

// Distinct enough to tell apart on a PDF page; repeats after eight signers.
export const SIGNER_COLORS = ['#2563eb', '#db2777', '#059669', '#d97706', '#7c3aed', '#0891b2', '#dc2626', '#4b5563'];

export const signerColor = (index) => SIGNER_COLORS[Math.max(0, index) % SIGNER_COLORS.length];

/** The contact the document goes to. When sending to many, each contact in turn. */
export const recipientSigner = () => ({ key: RECIPIENT_KEY, role: 'recipient', name: '', email: '' });

export const newSignerKey = () => `signer-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Name and email to show for a signer. `recipient` is the contact's own
 * name/email; when sending to many there is no single contact yet.
 */
export const signerDisplay = (signer, { recipient = {}, forMany = false } = {}) => {
  if (signer?.role === 'recipient') {
    return forMany
      ? { name: 'Each recipient', email: 'Filled in for each contact you send to' }
      : { name: recipient.fullName || 'Contact', email: recipient.email || '' };
  }
  return { name: signer?.name || '', email: signer?.email || '' };
};

/** Move a signer earlier (-1) or later (+1) in the signing order. */
export const moveSigner = (signers, key, delta) => {
  const from = signers.findIndex((s) => s.key === key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= signers.length) return signers;
  const next = [...signers];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
};

/** How many signature/initials fields each signer has, by key. */
export const signerFieldCounts = (boxes = []) =>
  boxes.reduce((counts, box) => {
    if (SIGNABLE_TYPES.has(box?.type) && box.signer_key) {
      counts[box.signer_key] = (counts[box.signer_key] || 0) + 1;
    }
    return counts;
  }, {});

/**
 * The first thing stopping a send, in words the sender can act on, or null.
 */
export const signerProblem = (signers, boxes, { recipient = {}, forMany = false } = {}) => {
  if (!boxes.some((box) => box?.type === 'signature')) {
    return 'Place at least one Signature field.';
  }

  const seenEmails = new Map();
  if (!forMany && recipient.email && signers.some((s) => s.role === 'recipient')) {
    seenEmails.set(recipient.email.trim().toLowerCase(), signerDisplay(signers[0], { recipient }).name);
  }

  for (const signer of signers) {
    if (signer.role === 'recipient') continue;
    const name = (signer.name || '').trim();
    const email = (signer.email || '').trim().toLowerCase();
    if (!name) return 'Every signer needs a name.';
    if (!EMAIL_REGEX.test(email)) return `${name} needs a valid email address.`;
    if (seenEmails.has(email)) return `${name} has the same email as ${seenEmails.get(email)}. Each signer needs their own email.`;
    seenEmails.set(email, name);
  }

  const counts = signerFieldCounts(boxes);
  for (const signer of signers) {
    if (!counts[signer.key]) {
      const { name } = signerDisplay(signer, { recipient, forMany });
      return `${name} has no Signature or Initials field yet. Place one, or remove them from the signers.`;
    }
  }

  return null;
};

/** The `signer` object the backend expects on a signature/initials field. */
export const signerPayload = (signers, key, { recipient = {}, forMany = false } = {}) => {
  const index = signers.findIndex((s) => s.key === key);
  if (index < 0) return null;
  const signer = signers[index];

  if (signer.role === 'recipient') {
    // The backend fills in the contact's name and email (and, when sending to
    // many, a different contact on each copy).
    return {
      role: 'recipient',
      id: null,
      name: forMany ? '' : (recipient.fullName || ''),
      email: forMany ? '' : (recipient.email || ''),
      order_number: index + 1,
    };
  }

  return { id: null, name: signer.name.trim(), email: signer.email.trim(), order_number: index + 1 };
};
